import _ from 'lodash';
import { useState } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { selectShowPercent } from '../../../selectors/settings';
import { exportGradebook } from '../../InstructorGradebookContainer/InstructorGradebook/export';
import useModalStates from '../useModalStates';
import { useFullRoster } from '../../../hooks/useFullRoster';
import { updateAlert } from '../../../actions/gradebook';
import { sendAnalyticsEvent } from '../../../actions/sendAnalytics';
import { clickExportGradebook } from '../../../actions/analyticsEvents';
import {
  DEFAULT_ASSIGNED_TO,
  EVERYONE_OPTION,
  EXPORT_GRADE_FORMAT_OPTIONS,
  EXPORT_OPTIONS,
} from '../../../utils/constants';
import { createGradebookAlert } from '../../../utils/util';

const EXPORT_SUCCESS_FOCUS_DELAY_MS = 500;
const EXPORT_ERROR_FOCUS_DELAY_MS = 500;

export const useExportDialog = ({ courseShortName }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { exportDialog } = useModalStates();
  const state = useStore().getState();
  const dispatch = useDispatch();
  const { gradebook } = state;

  const { courseId, assignments, settings, exceptions } = gradebook;
  const roster = useFullRoster(courseId);

  const requireStudentId = _.find(settings, { key: 'requireStudentId' });
  const showPercentSetting = selectShowPercent(state);

  const [exportOptions, setExportOptions] = useState({
    ...EXPORT_OPTIONS,
    ...(requireStudentId &&
      requireStudentId.value === 'true' && {
        INCLUDE_STUDENT_ID: true,
      }),
    ...(showPercentSetting && {
      GRADE_FORMAT: EXPORT_GRADE_FORMAT_OPTIONS.PERCENTAGES,
    }),
    selectedStudents: [EVERYONE_OPTION],
  });

  const toogleLoading = () => setIsLoading((bool) => !bool);

  const handleExportOptions = (values = {}) => {
    setExportOptions((prevState) => ({
      ...prevState,
      ...values,
    }));
  };

  const handleExportGradebook = async () => {
    setIsLoading(true);
    dispatch(sendAnalyticsEvent(clickExportGradebook(courseId)));
    const studentIds = getUniqueStudentIds(exportOptions.selectedStudents);
    const exportData = {
      exportOptions,
      studentIds,
      course: {
        roster,
        id: courseId,
        data: {
          assignments,
          exceptions,
          short_name: courseShortName,
        },
      },
    };

    const showExportAlertAndCloseDialog = (alertOptions) => {
      dispatch(updateAlert(createGradebookAlert(alertOptions)));
      exportDialog.toggle();
    };

    try {
      await exportGradebook(exportData);
      showExportAlertAndCloseDialog({
        type: 'success',
        text: 'Gradebook exported successfully.',
        focusOnShow: true,
        focusDelay: EXPORT_SUCCESS_FOCUS_DELAY_MS,
      });
    } catch (_e) {
      showExportAlertAndCloseDialog({
        type: 'error',
        text: 'Gradebook export failed. Please try again.',
        focusOnShow: true,
        focusDelay: EXPORT_ERROR_FOCUS_DELAY_MS,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getUniqueStudentIds = (selectedStudents) => {
    const isEveryoneSelected = selectedStudents.some(
      (item) => item.studentId === DEFAULT_ASSIGNED_TO,
    );
    if (isEveryoneSelected) {
      return [];
    }

    const studentIds = selectedStudents.flatMap((item) => {
      if (item.studentIds) {
        return item.studentIds.map((id) => id);
      }
      return item.studentId;
    });
    return [...new Set(studentIds)];
  };

  return {
    isLoading,
    setIsLoading,
    toogleLoading,
    handleExportGradebook,
    handleExportOptions,
    exportOptions,
    toggleModal: exportDialog.toggle,
    isShowing: exportDialog.isShowing,
    roster,
  };
};
